<template>
  <div
    @click="onClickElement"
    class="form-field-card w-100 d-flex align-items-center justify-content-between"
    :class="[
      element.type,
      element.name,
      { editable: !isPreview, 'hidden-field': isHiddenInPreview },
    ]"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
    :data-index="rowIndex"
    :id="`form-element-${element.id}`"
    @mouseenter="$emit('mouseenter', $event)"
    @mouseleave="$emit('mouseleave', $event)"
  >
    <div
      :draggable="draggable && this.isAllowToDrag"
      @dragstart="onDragStart"
      @dragend="onDragEnd"
      @drop="onDrop"
      class="form-field w-100 position-relative"
      :id="`form-field-${element.id}`"
      :class="[
        { active: isActivePropsElement(element) },
        dragHighlightClass,
        { 'prevent-pointer': !isPreview && !isAskAiModalVisible },
        { 'show-spacing': isSpacingVisible },
      ]"
      @mousedown="handleMouseDownOnFormElement"
    >
      <FormTitleElement
        v-if="isShowElement(ELEMENTS_ENUM.FORM_TITLE)"
        :element="element"
        :titleStyle="getTitleStyle"
      />
      <FormTextblockElement
        v-else-if="isShowElement(ELEMENTS_ENUM.FORM_TEXTBLOCK)"
        :element="element"
        :textStyle="getTitleStyle"
      />
      <FormFileElement
        v-else-if="isShowElement(ELEMENTS_ENUM.FORM_FILE)"
        :element="element"
        v-model="element.value"
        :labelStyle="getLabelStyle"
        :fieldStyle="getFieldStyle"
      />
      <FormInputElement
        v-else-if="isShowElement(ELEMENTS_ENUM.FORM_INPUT)"
        :element="element"
        :isAskAiModalVisible="isAskAiModalVisible"
        v-model="element.value"
        :labelStyle="getLabelStyle"
        :fieldStyle="getFieldStyle"
        :disabled="disabled"
      />
      <FormTextareaElement
        v-else-if="isShowElement(ELEMENTS_ENUM.FORM_TEXTAREA)"
        :element="element"
        :isAskAiModalVisible="isAskAiModalVisible"
        v-model="element.value"
        :labelStyle="getLabelStyle"
        :fieldStyle="getFieldStyle"
        :disabled="disabled"
      />
      <FormFormattedInputElement
        v-else-if="isShowElement(ELEMENTS_ENUM.FORM_FORMATTED_INPUT)"
        :element="element"
        :labelStyle="getLabelStyle"
        :fieldStyle="getFieldStyle"
        :disabled="disabled"
        v-model="element.value"
      />
      <FormOptionCardElement
        v-else-if="isShowElement(ELEMENTS_ENUM.FORM_OPTION_CARD)"
        :element="element"
        :isPreview="isPreview"
        :labelStyle="getLabelStyle"
        :fieldStyle="getFieldStyle"
        :brandedStyle="brandedStyle"
        @onOpenCardModal="onToggleCardModal"
        :disabled="disabled"
      />
      <FormToggleListElement
        ref="toggleListRef"
        v-else-if="isShowElement(ELEMENTS_ENUM.FORM_KLAPP_LIST)"
        :element="element"
        :elements="elements"
        :tabStructure="tabStructure"
        :tabData="tabData"
        :rowIndex="rowIndex"
        :colIndex="colIndex"
        :tabIndex="tabIndex"
        :isPreview="isPreview"
        :draggable="draggable"
        :dragItemId="dragItemId"
        :isActivePropsElement="isActivePropsElement"
        :labelStyle="getLabelStyle"
        :brandedStyle="brandedStyle"
        :isAskAiModalVisible="isAskAiModalVisible"
        :isDragInsideGrid="isDragInsideGrid"
        :toggleListState="toggleListState"
        :layoutSetting="layoutSetting"
        :formSettings="formSettings"
        :brandedSubmitBtnStyle="brandedSubmitBtnStyle"
        @onToggleListStateChange="
          (elementId, isOpen) =>
            $emit('onToggleListStateChange', elementId, isOpen)
        "
        @dragend="onDragEnd"
        @editElement="onElementEdit"
        @setDragItemId="setDragItemId"
        @removeElement="onRemoveElement"
        @duplicateElement="onDuplicateElement"
        @insertNewElement="onInsertNewElement"
        @tabButtonSubmit="(event) => $emit('tabButtonSubmit', event)"
      />
      <FormToggleSwitchElement
        v-else-if="isShowElement(ELEMENTS_ENUM.FORM_TOGGLE_SWITCH)"
        :element="element"
        :isPreview="isPreview"
        :labelStyle="getLabelStyle"
        :brandedStyle="brandedStyle"
      />
      <FormHtmlElement
        v-else-if="isShowElement(ELEMENTS_ENUM.FORM_HTML)"
        :element="element"
        :isPreview="isPreview"
        :labelStyle="getLabelStyle"
      />
      <FormAudioInputElement
        v-else-if="isShowElement(ELEMENTS_ENUM.FORM_AUDIO_INPUT)"
        :element="element"
        :elements="elements"
        :labelStyle="getLabelStyle"
        :fieldStyle="getFieldStyle"
        :disabled="disabled"
      />
      <FormOptionElement
        v-else-if="isShowElement(ELEMENTS_ENUM.FORM_OPTION)"
        :element="element"
        :isPreview="isPreview"
        :labelStyle="getLabelStyle"
        :fieldStyle="getFieldStyle"
        :brandedStyle="brandedStyle"
        :isAskAiModalVisible="isAskAiModalVisible"
      />
      <FormSelectElement
        v-else-if="isShowElement(ELEMENTS_ENUM.FORM_SELECT)"
        :element="element"
        :isPreview="isPreview"
        :labelStyle="getLabelStyle"
        :fieldStyle="getFieldStyle"
        :brandedStyle="brandedStyle"
        :disabled="disabled"
      />

      <FormWrapperElement
        v-else-if="isShowElement(ELEMENTS_ENUM.FORM_WRAPPER)"
        :element="element"
        :elements="elements"
        :dragItemId="dragItemId"
        :tabStructure="tabStructure"
        :tabData="tabData"
        :rowIndex="rowIndex"
        :colIndex="colIndex"
        :tabIndex="tabIndex"
        :isDragInsideGrid="isDragInsideGrid"
        :isAskAiModalVisible="isAskAiModalVisible"
        :draggable="draggable"
        :isActivePropsElement="isActivePropsElement"
        :isPreview="isPreview"
        :brandedStyle="brandedStyle"
        :labelStyle="getLabelStyle"
        :toggleListState="toggleListState"
        :layoutSetting="layoutSetting"
        :formSettings="formSettings"
        :brandedSubmitBtnStyle="brandedSubmitBtnStyle"
        @setDragItemId="setDragItemId"
        @editElement="onElementEdit"
        @removeElement="onRemoveElement"
        @duplicateElement="onDuplicateElement"
        @insertNewElement="onInsertNewElement"
        @onToggleListStateChange="
          (elementId, isOpen) =>
            $emit('onToggleListStateChange', elementId, isOpen)
        "
        @dragend="onDragEnd"
        @tabButtonSubmit="(event) => $emit('tabButtonSubmit', event)"
      />

      <FormResultFrame
        v-else-if="isShowElement(ELEMENTS_ENUM.FORM_RESULT_FRAME)"
        :isPreview="isPreview"
        :responseData="responseData"
      />
      <FormNavigationButtonsElement
        v-else-if="isNavButtonsElement && tabStructure?.tabs?.length == 1"
        :element="element"
        :tabStructure="tabStructure"
        :tabIndex="tabIndex"
        :settings="formSettings"
        :layoutSetting="layoutSetting"
        :brandedSubmitBtnStyle="brandedSubmitBtnStyle"
        @submit="(event) => $emit('tabButtonSubmit', event)"
      />

      <FormElementTools
        v-if="
          !isPreview &&
          !dragItemId &&
          !isAskAiModalVisible &&
          areEditToolsVisible
        "
        :element="element"
        :isActivePropsElement="isActivePropsElement"
        :singleInstanceElementTypes="singleInstanceElementTypes"
        @removeElement="onRemoveElement"
        @duplicateElement="onDuplicateElement"
        @insertNewElement="onInsertNewElement"
      />
    </div>

    <OptionCardModal
      v-if="selectedOption"
      :htmlString="selectedOption.htmlContent"
      :margin="selectedOption.margin"
      :position="element.settings.optionModalPosition"
      @close="onToggleCardModal"
    />
  </div>
</template>

<script>
import OptionCardModal from '@/components/FormBuilder/core/OptionCardModal.vue';
import FormTitleElement from '@/components/FormBuilder/elements/FormTitleElement.vue';
import FormInputElement from '@/components/FormBuilder/elements/FormInputElement.vue';
import FormTextareaElement from '@/components/FormBuilder/elements/FormTextareaElement.vue';
import FormOptionCardElement from '@/components/FormBuilder/elements/FormOptionCardElement.vue';
import FormOptionElement from '@/components/FormBuilder/elements/FormOptionElement.vue';
import FormSelectElement from '@/components/FormBuilder/elements/FormSelectElement.vue';
import FormToggleSwitchElement from '@/components/FormBuilder/elements/FormToggleSwitchElement.vue';
import FormToggleListElement from '@/components/FormBuilder/elements/FormToggleListElement.vue';
import FormFileElement from '@/components/FormBuilder/elements/FormFileElement.vue';
import FormHtmlElement from '@/components/FormBuilder/elements/FormHtmlElement.vue';
import FormAudioInputElement from '@/components/FormBuilder/elements/FormAudioInputElement.vue';
import FormTextblockElement from '@/components/FormBuilder/elements/FormTextblockElement.vue';
import FormFormattedInputElement from '@/components/FormBuilder/elements/FormFormattedInputElement.vue';
import FormWrapperElement from '@/components/FormBuilder/elements/FormWrapperElement.vue';
import FormResultFrame from '@/components/FormBuilder/elements/FormResultFrame.vue';
import FormNavigationButtonsElement from '@/components/FormBuilder/elements/FormNavigationButtonsElement.vue';
import FormElementTools from '@/components/FormBuilder/core/FormElementTools.vue';
import { ELEMENTS_ENUM } from '@/utils/constants/FormBuilderConstants.js';
import { hexToRgba } from '@/utils/helpers';

export default {
  name: 'FormBuilderElement',
  data() {
    return {
      selectedOption: null,
      isAllowToDrag: true,
      isShowCardModal: false,
      ELEMENTS_ENUM,
    };
  },
  props: {
    element: {
      type: Object,
      required: true,
    },
    elements: {
      type: Array,
      required: true,
    },
    isActivePropsElement: {
      type: Function,
      default: () => false,
    },
    rowIndex: {
      type: [Number, String],
      required: true,
    },
    colIndex: {
      type: [Number, String],
      required: true,
    },
    tabIndex: {
      type: [Number, String],
      required: true,
    },
    dragPosition: {
      type: String,
      default: null,
    },
    dragItemId: {
      type: Number,
      default: null,
    },
    draggable: {
      type: Boolean,
      default: false,
    },
    isPreview: { type: Boolean, default: false },
    isAskAiModalVisible: { type: Boolean, default: false },
    labelStyle: { type: Object, default: () => {} },
    fieldStyle: { type: Object, default: () => {} },
    brandedStyle: { type: Object, default: () => {} },
    disabled: { type: Boolean, default: false },
    responseData: {
      type: Object,
      default: () => ({
        responsePayload: null,
        responseType: null,
        isLoading: false,
      }),
    },
    tabStructure: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    tabData: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    isMultipleElementInCol: {
      type: Boolean,
      default: false,
    },
    isDragInsideGrid: {
      type: Boolean,
      default: () => false,
    },
    toggleListState: {
      type: Object,
      default: () => {},
    },
    singleInstanceElementTypes: {
      type: Array,
      default: () => [],
    },
    layoutSetting: {
      type: Object,
      default: () => ({}),
    },
    formSettings: {
      type: Object,
      default: () => ({}),
    },
    brandedSubmitBtnStyle: {
      type: Object,
      default: () => ({}),
    },
  },
  methods: {
    onToggleCardModal(option = null) {
      if (option?.isModalVisible) {
        this.selectedOption = {
          htmlContent: option.modalConfig?.htmlContent || '',
          margin: option.modalConfig?.margin || 'small-margin',
        };
      } else {
        this.selectedOption = null;
      }
    },
    handleMouseDownOnFormElement(event) {
      if (this.isAskAiModalVisible) {
        event.preventDefault();
      }
    },
    onClickElement(event) {
      if (!this.isAskAiModalVisible) {
        event.stopPropagation();
      }
      if (this.isNavButtonsElement) return;
      this.onElementEdit();
    },
    onElementEdit(element) {
      if (!this.isPreview) this.$emit('editElement', element || this.element);
    },

    isShowElement(name) {
      if (name === ELEMENTS_ENUM.FORM_INPUT) {
        return [
          'text',
          'number',
          'email',
          'date',
          'datetime-local',
          'time',
          'hidden',
          'model',
        ].includes(this.element.type);
      } else if (name === ELEMENTS_ENUM.FORM_TEXTAREA) {
        return this.element.type === 'textarea';
      } else if (name === ELEMENTS_ENUM.FORM_TITLE) {
        return this.element.type === 'title';
      } else if (name === ELEMENTS_ENUM.FORM_KLAPP_LIST) {
        return this.element.type === 'togglelist';
      } else if (name === ELEMENTS_ENUM.FORM_OPTION_CARD) {
        return (
          this.element.settings.appearance &&
          this.element.settings.appearance === 'card'
        );
      } else if (name === ELEMENTS_ENUM.FORM_MAIN) {
        return !(
          this.isPreview &&
          (this.element.type === 'hidden' || this.element?.settings?.hidden)
        );
      } else if (name === ELEMENTS_ENUM.FORM_TOGGLE_SWITCH) {
        return this.element.type === 'toggleswitch';
      } else if (name === ELEMENTS_ENUM.FORM_FILE) {
        return this.element.type === 'file';
      } else if (name === ELEMENTS_ENUM.FORM_HTML) {
        return this.element.type === 'html';
      } else if (name === ELEMENTS_ENUM.FORM_AUDIO_INPUT) {
        return this.element.type === 'audioinput';
      } else if (name === ELEMENTS_ENUM.FORM_OPTION) {
        return ['radio', 'checkbox'].includes(this.element.type);
      } else if (name === ELEMENTS_ENUM.FORM_SELECT) {
        return this.element.type === 'select';
      } else if (name === ELEMENTS_ENUM.FORM_TEXTBLOCK) {
        return this.element.type === 'textblock';
      } else if (name === ELEMENTS_ENUM.FORM_FORMATTED_INPUT) {
        return this.element.type === 'formatted_number';
      } else if (name === ELEMENTS_ENUM.FORM_WRAPPER) {
        return this.element.type === 'wrapper';
      } else if (name === ELEMENTS_ENUM.FORM_RESULT_FRAME) {
        return this.element.type === 'resultframe';
      } else {
        return false;
      }
    },
    onRemoveElement(element) {
      this.$emit('removeElement', element);
    },
    onDuplicateElement(element) {
      this.$emit('duplicateElement', element);
    },
    onInsertNewElement(elementId, position) {
      this.$emit('insertNewElement', elementId, position);
    },
    onDragStart(event) {
      if (event.target.closest('.edit-mode-tool-wrapper')) {
        return;
      }
      event.stopPropagation();
      if (this.isShowElement('formToggleList')) {
        this.$emit('onToggleListStateChange', this.element.id, false);
      }
      if (this.isAllowToDrag) this.$emit('dragstart', event, this.element);
    },
    onDragOver(event) {
      if (this.isMultipleElementInCol) return;
      this.$emit('dragover', event);
    },
    onDragLeave(event) {
      this.$emit('dragleave', event);
    },
    onDragEnd() {
      if (this.isAllowToDrag) this.$emit('dragend');
      this.isAllowToDrag = true;
    },
    onDrop(event) {
      this.$emit('drop', event);
    },
    setDragItemId(id) {
      this.$emit('setDragItemId', id);
    },
  },
  computed: {
    isHiddenInPreview() {
      return (
        this.isPreview &&
        (this.element.type === 'hidden' || this.element?.settings?.hidden)
      );
    },
    dragHighlightClass() {
      if (this.isMultipleElementInCol) return '';
      if (this.dragPosition === 'top') return 'highlight-above';
      if (this.dragPosition === 'bottom') return 'highlight-below';
      return '';
    },
    getBrandedFontStyle() {
      return {
        fontFamily: this.brandedStyle?.fontType
          ? `${this.brandedStyle?.fontType} !important`
          : '',
      };
    },
    getTitleStyle() {
      return {
        color: this.getLabelStyle?.color || '',
        ...this.getBrandedFontStyle,
      };
    },
    getLabelStyle() {
      return {
        color: this.brandedStyle?.textColor
          ? `${this.brandedStyle?.textColor} !important`
          : '',
        ...this.getBrandedFontStyle,
      };
    },
    getFieldStyle() {
      return {
        color: this.brandedStyle?.textColor
          ? `${this.brandedStyle?.textColor} !important`
          : '',
        placeholderColor: hexToRgba(this.brandedStyle?.textColor, 0.4),
        textColor: this.brandedStyle?.textColor,
        borderColor: this.brandedStyle?.primaryBrandColor
          ? `${this.brandedStyle?.primaryBrandColor} !important`
          : '',
        outlineColor: this.brandedStyle?.primaryBrandColor
          ? `${this.brandedStyle?.primaryBrandColor} `
          : '',
        borderRadius:
          this.brandedStyle &&
          this.brandedStyle.hasOwnProperty('primaryBordarRadius')
            ? `${this.brandedStyle?.primaryBordarRadius}px !important`
            : '',
        primaryBrandColor: this.brandedStyle?.primaryBrandColor
          ? `${this.brandedStyle?.primaryBrandColor} `
          : '',
        ...this.getBrandedFontStyle,
      };
    },
    areEditToolsVisible() {
      const elementTypeToHide = ['wrapper', 'navButtons'];
      return !elementTypeToHide.includes(this.element.type);
    },
    isSpacingVisible() {
      const elementTypeToHide = ['wrapper', 'navButtons'];
      return !elementTypeToHide.includes(this.element.type);
    },
    isNavButtonsElement() {
      return this.element?.type === 'navButtons';
    },
  },
  components: {
    FormTitleElement,
    FormInputElement,
    FormTextareaElement,
    FormOptionCardElement,
    OptionCardModal,
    FormToggleListElement,
    FormToggleSwitchElement,
    FormFileElement,
    FormHtmlElement,
    FormAudioInputElement,
    FormOptionElement,
    FormSelectElement,
    FormTextblockElement,
    FormFormattedInputElement,
    FormWrapperElement,
    FormElementTools,
    FormResultFrame,
    FormNavigationButtonsElement,
  },
};
</script>

<style scoped>
.form-field-card {
  gap: 10px;
}

.form-field-card.resultframe {
  align-items: stretch !important;
}

.form-field-card.editable {
  user-select: none;
}

.form-field-card.editable .form-field {
  padding: 15px 12px !important;
}
.form-field-card.editable .form-field:has(.form-toggle-list-element-wrapper) {
  padding-left: 0 !important;
}
.form-field-card.navButtons.editable .form-field {
  padding: 0 !important;
}
.form-field-card .form-field {
  background: transparent;
}

.form-field-card.resultframe .form-field {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 140px;
}

.form-field-card .form-field.show-spacing {
  padding-bottom: 31px;
}

.form-field-card.resultframe .form-field.show-spacing {
  padding-bottom: 0;
}
.form-field-card
  .form-field:has(.form-html-element-wrapper):not(.prevent-pointer) {
  padding: 0 !important;
  margin: 0 !important;
}

.form-field-card .form-field.prevent-pointer {
  cursor: pointer;
}
.form-field-card .form-field.prevent-pointer ::v-deep .form-label,
.form-field-card .form-field.prevent-pointer ::v-deep .form-input {
  pointer-events: none;
}

/* card drag highlight css  */

.form-field-card .form-field.highlight-above::before,
.form-field-card .form-field.highlight-below::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 4px;
  z-index: 1;
  background-color: #86c3f4;
}

.form-field-card .form-field.highlight-above::before {
  top: -2px !important;
}

.form-field-card .form-field.highlight-below::after {
  bottom: -2px !important;
}

.form-item-wrapper
  .tab-inner-container
  .form-grid-row:last-child
  .form-field.highlight-below::after {
  bottom: 0px !important;
}

.form-item-wrapper
  .tab-inner-container
  .form-grid-row:first-child
  .form-field.highlight-above::before {
  top: 0px !important;
}

/* base on screen size title spacing css  */
.form-builder-container
  .form-item-wrapper
  .form-field-card
  .form-field:has(> .form-title-element-wrapper) {
  padding-bottom: 35px !important;
}
.form-builder-container
  .preview-mode
  .form-item-wrapper
  .form-field-card
  .form-field:has(> .form-title-element-wrapper) {
  padding-bottom: 51px !important;
}
.form-builder-container .form-item-wrapper .form-field-card.hidden-field {
  display: none !important;
}
@media only screen and (max-width: 767px) {
  .form-builder-container
    .preview-mode
    .form-item-wrapper
    .form-field-card
    .form-field:has(> .form-title-element-wrapper) {
    padding-bottom: 51px;
  }
}
</style>
